import { Module } from 'core/Module';
import { Events } from 'core/events/Events';

interface MarkerPoint {
	lat: number;
	long: number;
	image?: string;
}

export class GoogleMap extends Module {
	public config = {
		lat: 51.5491144,
		long: -0.07688,
		style: [
			{
				featureType: 'all',
				elementType: 'labels.text.fill',
				stylers: [{ saturation: 36 }, { color: '#000000' }, { lightness: 40 }]
			},
			{
				featureType: 'all',
				elementType: 'labels.text.stroke',
				stylers: [{ visibility: 'on' }, { color: '#000000' }, { lightness: 16 }]
			},
			{ featureType: 'all', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
			{
				featureType: 'administrative',
				elementType: 'geometry.fill',
				stylers: [{ color: '#000000' }, { lightness: 20 }]
			},
			{
				featureType: 'administrative',
				elementType: 'geometry.stroke',
				stylers: [{ color: '#000000' }, { lightness: 17 }, { weight: 1.2 }]
			},
			{ featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 20 }] },
			{ featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 21 }] },
			{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#000000' }, { lightness: 17 }] },
			{
				featureType: 'road.highway',
				elementType: 'geometry.stroke',
				stylers: [{ color: '#000000' }, { lightness: 29 }, { weight: 0.2 }]
			},
			{ featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 18 }] },
			{ featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 16 }] },
			{ featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 19 }] },
			{ featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }, { lightness: 17 }] }
		],
		zoom: 16,
		type: 'ROADMAP',
		control: 'LEFT_BOTTOM',
		backgroundColour: '#333333',
		markerImage: '/img/ui/google-map-marker.png',
		scrollWheel: false,
		directionsSelector: null,
		replaceAtWidth: 1000,
		replaceImage: '/img/ui/mobile-map.jpg',
		replaceSelector: null
	};

	private _markerData: Array<MarkerPoint> = [];
	private _map: google.maps.Map;

	//Mapping features:
	private _directionsStep: number = 1;

	private _$directionsContainer: HTMLElement;
	private _$directionsTypes: HTMLElement;
	private _$directionsAutoComplete: HTMLInputElement;
	private _$directionsTitle: HTMLElement;

	private _travelMode: google.maps.TravelMode;
	private _directionsService: google.maps.DirectionsService;
	private _directionsDisplay: google.maps.DirectionsRenderer;
	private _place: any; //What is this type?
	private _autocomplete: google.maps.places.Autocomplete;

	public create(): void {
		if (this.config.replaceImage && window.innerWidth <= this.config.replaceAtWidth) {
			this._replaceMap();
			this.destroy();
			return;
		}

		this._createMap();
		this._setupMainMarker();
		this._createMarkers();
		if (this.config.directionsSelector) {
			this._setupDirections();
		}
		this.resize();
	}

	public resize(): void {
		(this.$container as HTMLElement).style.height = `${window.innerHeight * 0.9}px`;
	}

	//Private
	//------------------------------------------------------------------------------------------
	private _createMap(): void {
		var center = new google.maps.LatLng(this.config.lat, this.config.long);
		var mapOptions: google.maps.MapOptions = {
			zoom: this.config.zoom,
			center: center,
			mapTypeId: google.maps.MapTypeId[this.config.type],
			mapTypeControl: false,
			panControl: false,
			scrollwheel: this.config.scrollWheel,
			styles: this.config.style as any,
			zoomControlOptions: {
				style: google.maps.ZoomControlStyle.LARGE,
				position: google.maps.ControlPosition[this.config.control]
			},
			streetViewControlOptions: { position: google.maps.ControlPosition[this.config.control] },
			backgroundColor: this.config.backgroundColour
		};

		this._map = new google.maps.Map(this.$container, mapOptions);
	}

	private _setupMainMarker(): void {
		this._markerData.push({ lat: this.config.lat, long: this.config.long, image: this.config.markerImage });
	}

	private _createMarkers(): void {
		if (this._markerData.length === 0) {
			return;
		}

		this._markerData.forEach((data: MarkerPoint) => this._createMarker(data));
	}

	private _createMarker(data: MarkerPoint): void {
		var icon = {
			url: data.image, // url
			scaledSize: new google.maps.Size(74, 96), // scaled size
			origin: new google.maps.Point(0, 0), // origin
			anchor: new google.maps.Point(37, 96) // anchor
		};

		var point = new google.maps.LatLng(data.lat, data.long);
		new google.maps.Marker({
			draggable: false,
			icon: icon,
			map: this._map,
			position: point
		});
	}

	private _setupDirections(): void {
		this._$directionsContainer = document.querySelector(this.config.directionsSelector);

		this._directionsService = new google.maps.DirectionsService();
		this._directionsDisplay = new google.maps.DirectionsRenderer({
			polylineOptions: {
				strokeColor: '#01dcf7',
				strokeWeight: 5
			},
			markerOptions: { visible: false, position: null }
		});

		this._directionsDisplay.setMap(this._map);

		//Add auto comlete feature ----------------------------------------------------------------
		this._autocomplete = new google.maps.places.Autocomplete(
			this._$directionsContainer.querySelector('.googlemap__autocomplete') as HTMLInputElement,
			{ types: ['geocode'] }
		);

		// When the user selects an address from the dropdown, populate the address
		// fields in the form.
		this._autocomplete.addListener('place_changed', () => this._fillInAddress());

		//Add travel type button events:
		Events.on('click', `${this.config.directionsSelector} .googlemap__traveltype`, (e: MouseEvent) => {
			//Get the type number:
			let type: string = (e.target as HTMLElement).getAttribute('data-travel-type');
			let travelMode: google.maps.TravelMode = google.maps.TravelMode[type];

			this._setTravelMode(travelMode);
			return false;
		});

		Events.on('click', `${this.config.directionsSelector}`, (e: MouseEvent) => {
			this._directionsHolderPress();
			return false;
		});

		//Set refs
		this._$directionsTypes = this._$directionsContainer.querySelector(`.googlemap__traveltypes`) as HTMLElement;
		this._$directionsAutoComplete = this._$directionsContainer.querySelector(
			`.googlemap__autocomplete`
		) as HTMLInputElement;
		this._$directionsTitle = this._$directionsContainer.querySelector(`.googlemap__title`) as HTMLElement;
	}

	private _directionsHolderPress(): void {
		if (this._directionsStep > 1) {
			return;
		}

		this._setupDirectionsStep(this._directionsStep + 1);
	}

	private _fillInAddress() {
		// Get the place details from the autocomplete object.
		this._place = this._autocomplete.getPlace();

		let point: google.maps.LatLng | string;

		if (this._place.geometry) {
			let lat = this._place.geometry.location.lat();
			let lng = this._place.geometry.location.lng();
			point = new google.maps.LatLng(lat, lng);
		} else {
			point = this._place.name;
		}

		this._calculateAndDisplayRoute(point);
	}



	private _setTravelMode(mode: google.maps.TravelMode) {
		this._travelMode = mode;
		this._setupDirectionsStep(this._directionsStep + 1);
	}

	private _calculateAndDisplayRoute(destinationPoint: google.maps.LatLng | string) {
		let originPoint: google.maps.LatLng = new google.maps.LatLng(this.config.lat, this.config.long);

		this._directionsService.route(
			{
				origin: originPoint,
				destination: destinationPoint,
				travelMode: this._travelMode
			},
			(response, status: google.maps.DirectionsStatus) => {
				if (status === google.maps.DirectionsStatus.OK) {
					this._directionsDisplay.setDirections(response);
					this._setupDirectionsStep(1);
				} else {
					window.alert('Sorry location not found, please try again.');
				}
			}
		);
	}

	private _setupDirectionsStep(step: number): void {
		this._directionsStep = step;

		if (this._directionsStep === 1) {
			//Reset / show start
			this._$directionsAutoComplete.style.display = 'none';
			gsap.TweenMax.to(this._$directionsContainer, 0.3, { height: 70 });
			this.DOM.setText(this._$directionsTitle, 'Get new directions?');
		} else if (this._directionsStep === 2) {
			//Show Travel types
			this._$directionsTypes.style.display = 'inline-block';
			this.DOM.setText(this._$directionsTitle, 'How you traveling?');

			//Animate in -------------------------------------------
			gsap.TweenMax.to(this._$directionsContainer, 0.3, { height: 180 });
			gsap.TweenMax.set(this._$directionsTypes, { autoAlpha: 1 });
			this.DOM.addClass(this._$directionsTypes, '-active');
			gsap.TweenMax.staggerFromTo(
				`${this.config.directionsSelector} .googlemap__traveltype`,
				0.4,
				{ scale: 0 },
				{ scale: 1, delay: 0.25 },
				0.06
			);
		} else if (this._directionsStep === 3) {
			//Show location input
			this._$directionsTypes.style.display = 'none';
			this._$directionsAutoComplete.style.display = 'inline-block';
			this._$directionsAutoComplete.value = '';
			gsap.TweenMax.fromTo(this._$directionsAutoComplete, 0.3, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0 });
			this.DOM.setText(this._$directionsTitle, 'Where you coming from?');
		}

		gsap.TweenMax.fromTo(this._$directionsTitle, 0.3, { autoAlpha: 0 }, { autoAlpha: 1 });
	}

	private _replaceMap(): void {
		let replaceElement = this.config.replaceSelector
			? document.querySelector(this.config.replaceSelector)
			: this.DOM.parent(this.$container);
		let newElement: string = `<img src='${this.config.replaceImage}' class='googlemap__replacement'  />`;

		if (this.config.replaceSelector) {
			this.DOM.setHTML(replaceElement, newElement);
		} else {
			this.$container.remove();
			this.DOM.append(replaceElement, newElement);
		}
	}
}
